import {
  Dispatch,
  ReactNode,
  SetStateAction,
  useCallback,
  useEffect,
  useState,
} from "react";
import { ProjectUser } from "./types";
import { IconUser } from "../ui/icons";
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "../ui/dropdown-menu";

interface Props {
  triggerElement: ReactNode;
  users: Array<ProjectUser>;
  onChange: (value: Array<string> | string) => void;
  open?: boolean;
  multiple?: boolean;
  title?: string;
  baseSelection?: Array<string>;
  clearString?: string;
  setOpen?: Dispatch<SetStateAction<boolean>>;
}

export function UserDropdown({
  open,
  setOpen,
  triggerElement,
  users,
  onChange,
  multiple = false,
  title = "Select user",
  clearString = "None",
  baseSelection,
}: Props) {
  const renderUserIcon = useCallback(
    (user: ProjectUser) =>
      user.picture ? (
        <img
          src={user.picture}
          alt={user.name}
          className="select-none focus:outline-none rounded-md h-6 w-6 p-[1px]"
          crossOrigin="anonymous"
        />
      ) : (
        <IconUser />
      ),
    []
  );

  const handleChange = useCallback(
    (id: string) => {
      if (multiple) {
        const newSelection = [...(baseSelection ?? [])];
        const prevIndex = newSelection?.indexOf(id);
        prevIndex == -1
          ? newSelection.push(id)
          : newSelection.splice(prevIndex, 1);
        onChange(newSelection);
      } else {
        if (baseSelection?.[0] === id) {
          onChange([]);
        } else {
          onChange([id]);
        }
      }
    },
    [baseSelection]
  );

  return (
    <DropdownMenu open={open} onOpenChange={setOpen}>
      <DropdownMenuTrigger
        asChild
        onPointerDown={(e) => e.stopPropagation()}
        onMouseDown={(e) => e.stopPropagation()}
      >
        {triggerElement}
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-[180px] ml-3">
        <div className="flex justify-between text-xs !p-1">{title}</div>
        <DropdownMenuSeparator />
        <DropdownMenuItem
          className="text-xs !py-0.5 !pl-0.5 gap-1.5 cursor-pointer"
          onClick={(e) => {
            onChange([]);
            e.stopPropagation();
          }}
          disabled={users?.length === 0}
        >
          <div className="border border-secondary-n-900 bg-transparent hover:bg-transparent h-6 w-6 text-xs rounded-md text-secondary-n-400 flex items-center justify-center">
            <IconUser />
          </div>
          {clearString}
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        {users?.map((user) => (
          <DropdownMenuCheckboxItem
            key={user.id}
            checked={(baseSelection ?? []).includes(user.id)}
            onCheckedChange={() => handleChange(user.id)}
            className="text-xs cursor-pointer !py-0.5 !pl-0.5 gap-1.5"
            onClick={(e) => e.stopPropagation()}
          >
            {renderUserIcon(user)}
            {user.name ?? user.email}
          </DropdownMenuCheckboxItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
